<script setup lang="ts">
import { computed, onBeforeUnmount, watch } from 'vue'
//Components
import BreadCrumbNavigationTemplate from 'Common/components/templates/BreadCrumbNavigationTemplate.vue'
//Modules
import AnnotatorView from 'Modules/pages/imageReview/AnnotatorView.vue'
import ImageReviewToolbar from 'Modules/pages/imageReview/ImageReviewToolbar.vue'
import ImageHistoryView from 'Modules/pages/imageReview/ImageHistoryView.vue'
import VisualizerView from 'Modules/pages/imageReview/VisualizerView.vue'
//API
import { useGetAirCraftsFiltered } from 'API/queries/iris/aircraft'
//Store
import { useImageReviewStore } from 'Store/pages/imageReview/imageReviewStore'
import { useFilterStore } from 'Store/filterStore'
//Utils
import { useRoute, useRouter, RouteRecordName } from 'vue-router'
import { storeToRefs } from 'pinia'
//Types
import type { BreadCrumbItem } from 'Common/types/utils/breadCrumbItem'
import { BreadCrumbNavigationLayoutType } from 'Common/types/utils/breadCrumbNavigationLayoutType'
import { ViewModes } from 'Modules/types/pages/imageReview/enums'
import { JeteyeRoutes } from 'Modules/RouterSetup'

const store = useImageReviewStore()
const { removeAnnotations, clearStagedAnnotations } = store.annotationsStore
const { resetAllMeasurementState } = store.measurementsStore
const { getNextImageUuid, getPreviousImageUuid } = store.imagesStore
const { cycleViewMode, setViewMode } = store.interfaceStore
const { viewMode } = storeToRefs(store.interfaceStore)
const { currentImageUuid } = storeToRefs(store.imagesStore)

const router = useRouter()
const route = useRoute()
const routeName = computed<RouteRecordName>(() => route.name)
const filterStore = useFilterStore()
const { queryParamObj, areFiltersReady } = storeToRefs(filterStore)
const imageUuidParam = computed<string | string[] | undefined>(() => {
  if (routeName.value === JeteyeRoutes.ImageReview) return queryParamObj.value?.inspectionImage
  return undefined
})
const inspectionInstanceUuidParam = computed<string | string[] | undefined>(() => {
  if (routeName.value === JeteyeRoutes.ImageReview) return queryParamObj.value?.inspectionInstance
  return undefined
})

// TODO - Rely on previously fetched data.
const { data: aircraftData } = useGetAirCraftsFiltered({
  variables: {
    filterQueryParams: { inspectionInstance: inspectionInstanceUuidParam },
  },
  enabled: areFiltersReady,
  refetchOnWindowFocus: false,
  staleTime: 300000,
})

watch([aircraftData.value], () => console.log(aircraftData.value))

//TODO Once we expand the image review page to allow multiple inspections to be viewed at a time,
//The aircraft breadcrumb will have to be able to properly navigate backwards
//Undecided on exact method but is detailed further in ticket: https://gitlab.com/near-earth/inspection/aci-portfolio/jeteye-review/-/issues/202
const breadCrumbs = computed((): Array<BreadCrumbItem> => {
  return [
    { label: 'Home', route: 'Home' },
    {
      label: aircraftData.value?.[0]?.tailNumber,
      params: { aircraftUUID: aircraftData.value?.[0]?.uuid },
      route: 'aircraft',
    },
    {
      label: 'Summary',
      route: 'SummaryReport',
      query: {
        inspectionInstance: route.query.inspectionInstance,
        aircraft: aircraftData.value?.[0]?.uuid,
      },
    },
    {
      label: 'Image Review',
    },
  ]
})

const pushImageRoute = (imageUuid): void => {
  if (imageUuid === currentImageUuid.value) return

  // remove all annotations so that they are not persisted to the next image
  //TODO Update respective stores to have these values reset/cascade when the
  //image uuid in the route changes.
  removeAnnotations()
  resetAllMeasurementState()
  clearStagedAnnotations() //TODO move to a watcher or reactive state
  filterStore.filtersModel.inspectionImage = [imageUuid]
}

const preCycleView = (event: KeyboardEvent): void => {
  if (event.ctrlKey) {
    cycleViewMode()
  }
}

const keyPressFunction = (event: KeyboardEvent): void => {
  const keyFunctionMapping = {
    37: () => {
      if (getPreviousImageUuid()) {
        clearStagedAnnotations() //TODO move to a watcher or reactive state
        pushImageRoute(getPreviousImageUuid())
      }
    },
    39: () => {
      if (getNextImageUuid()) {
        clearStagedAnnotations() //TODO move to a watcher or reactive state
        pushImageRoute(getNextImageUuid())
      }
    },
    86: (event) => {
      preCycleView(event)
    },
  }

  const callback = keyFunctionMapping[event.keyCode]

  if (typeof callback === 'function') callback(event)
}
document.addEventListener('keydown', keyPressFunction)

onBeforeUnmount(() => {
  clearStagedAnnotations()
  document.removeEventListener('keydown', keyPressFunction)
})
</script>

<template>
  <BreadCrumbNavigationTemplate
    :items="breadCrumbs"
    :layout="BreadCrumbNavigationLayoutType.BreadCrumbNavigationBlueLayout"
  >
    <template #main-content>
      <div class="h-full w-full relative box-border flex bg-je-darkgray">
        <div class="shrink">
          <ImageReviewToolbar :mode="viewMode" :set-mode="setViewMode" />
        </div>
        <div class="relative h-full grow min-w-[98%]">
          <template v-if="viewMode === ViewModes.Annotator">
            <AnnotatorView :push-image-route="pushImageRoute" />
          </template>
          <template v-else-if="viewMode === ViewModes.Visualizer">
            <VisualizerView :push-image-route="pushImageRoute" />
          </template>
          <template v-else-if="viewMode === ViewModes.History">
            <ImageHistoryView
              :inspection-instance-uuid="inspectionInstanceUuidParam"
              :push-image-route="pushImageRoute"
              :aircraft-data="aircraftData"
            />
          </template>
        </div>
      </div>
    </template>
  </BreadCrumbNavigationTemplate>
</template>
